import React, { useEffect, useRef, type ReactNode } from "react";

import { Box, Text } from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { Omittable } from "@salestrak/ui";

type DrilldownGroupTitleProps = Omittable<{
  onVisible?: () => void;
  children: ReactNode;
}>;

// Project cells in drilldown tables: the row's hover action owns the click,
// so the tag is label-only and inert.
export function DrilldownIssueLabel({
  issueId,
  name,
  workflowId,
}: Omittable<{
  issueId: string;
  name: string;
  workflowId?: string;
}>) {
  return (
    <EntityTag
      entity={{ kind: "issue", id: issueId, issue: { name }, workflowId }}
      display="label"
      weight="regular"
      interactive={false}
    />
  );
}

// Drilldown rows/groups only carry userId + name, so resolve the full
// workspace user for the chip and fall back to the bare name for members no
// longer in the workspace. Rows with no userId at all (unattributed metrics)
// degrade to plain text here — callers never re-implement that branch.
export const DrilldownMemberTag = React.memo(function DrilldownMemberTag({
  userId,
  name,
  size = "md",
  weight = "semibold",
}: Omittable<{
  userId?: string;
  name: string;
  size?: "sm" | "md";
  weight?: "regular" | "medium" | "semibold";
}>) {
  const { userMap } = useWorkspaceData();

  if (!userId) {
    return (
      <Text size="sm" truncate={1}>
        {name}
      </Text>
    );
  }

  const user = userMap.get(userId);

  return (
    <Box shrink>
      <EntityTag
        entity={{ kind: "user", id: userId, user: user ?? { name } }}
        size={size}
        weight={weight}
      />
    </Box>
  );
});

export const DrilldownGroupTitle = React.memo(function DrilldownGroupTitle({
  onVisible,
  children,
}: DrilldownGroupTitleProps) {
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!onVisible) return;
    const element = rootRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      onVisible();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisible();
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [onVisible]);

  return (
    <Box
      fill="flex"
      overflow="hidden"
      minWidth={0}
      ref={rootRef}
    >
      {children}
    </Box>
  );
});
